/**
 * React Error Boundary for LOCALE Web.
 *
 * Catches any unhandled JS error below it in the component tree,
 * reports it to Sentry, and renders a user-friendly fallback instead
 * of a blank white screen.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <App />
 *   </ErrorBoundary>
 *
 *   // Custom fallback:
 *   <ErrorBoundary fallback={(err, reset) => <MyFallback error={err} onRetry={reset} />}>
 *     <SomeWidget />
 *   </ErrorBoundary>
 */

import React, { type ReactNode, type ErrorInfo } from "react";
import { captureException } from "@/lib/analytics";

interface Props {
  children: ReactNode;
  /** Optional custom fallback renderer. Receives the error and a reset callback. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    captureException(error, {
      componentStack: info.componentStack ?? undefined,
      boundary: "ErrorBoundary",
    });
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    const { hasError, error } = this.state;

    if (!hasError || !error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset);
    }

    return <DefaultFallback error={error} onRetry={this.reset} />;
  }
}

// ─── Default fallback UI ──────────────────────────────────────────────────────

function DefaultFallback({
  error,
  onRetry,
}: {
  error: Error;
  onRetry: () => void;
}) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-5">
          <svg
            className="w-8 h-8 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>

        {/* Heading */}
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Something went wrong
        </h2>

        {/* Message — hide technical details in production */}
        <p className="text-gray-500 text-sm mb-6">
          {import.meta.env.PROD
            ? "An unexpected error occurred. Our team has been notified."
            : error.message}
        </p>

        {/* Actions */}
        <div className="flex gap-3 justify-center">
          <button
            onClick={onRetry}
            className="px-4 py-2 bg-[#1B3D8F] text-white rounded-lg text-sm font-medium hover:bg-[#152f6e] transition-colors"
          >
            Try again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            Reload page
          </button>
        </div>

        {/* Dev-only stack trace */}
        {!import.meta.env.PROD && (
          <pre className="mt-6 text-left text-xs bg-gray-100 rounded-lg p-3 overflow-auto max-h-48 text-red-700">
            {error.stack}
          </pre>
        )}
      </div>
    </div>
  );
}
